import { reactive } from 'vue'

export const AUTO_UPDATE_WINDOW_MS = 30 * 60_000
export const UPDATE_REMINDER_MS = 30 * 60_000
export const UPDATE_ACTIVATION_TIMEOUT_MS = 15_000
export const UPDATE_VISIT_KEY = 'maple-log:update-visit'
type UpdatePhase = 'idle' | 'saving' | 'activating' | 'reloading'

export interface UpdateEnvironment {
  now(): number
  navigationStartedAt?(): number
  visible(): boolean
  active(): boolean
  blocked(): boolean
  lock(value: boolean): void
  prepare(manual: boolean): Promise<void>
  activate(): Promise<void>
  reload(): void
  storage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
}

export function readUpdateVisit(storage: UpdateEnvironment['storage'], now: number, startedAt = now): { enteredAt: number; manualOnly: boolean } {
  try {
    const raw = storage().getItem(UPDATE_VISIT_KEY)
    storage().removeItem(UPDATE_VISIT_KEY)
    if (raw) {
      const marker = JSON.parse(raw) as { startedAt?: number; reloadedAt?: number; manualOnly?: boolean }
      if (typeof marker.startedAt === 'number' && typeof marker.reloadedAt === 'number'
        && Number.isFinite(marker.startedAt) && marker.startedAt <= marker.reloadedAt
        && marker.reloadedAt <= now && now - marker.reloadedAt < 60_000) return { enteredAt: marker.startedAt, manualOnly: marker.manualOnly === true }
    }
  } catch { /* Storage may be unavailable; a normal navigation starts a new visit. */ }
  return { enteredAt: startedAt, manualOnly: false }
}

export class AppUpdateManager {
  readonly state = reactive({
    ready: false, pending: false, expanded: false, manualOnly: false,
    snoozedUntil: 0, phase: 'idle' as UpdatePhase, error: '' as '' | 'save' | 'activation',
  })
  readonly enteredAt: number
  private reloadReady = false
  private operation: Promise<void> | undefined
  private activationDone: (() => void) | undefined
  private reminder: ReturnType<typeof setTimeout> | undefined
  private disposed = false

  constructor(private readonly env: UpdateEnvironment) {
    const visit = readUpdateVisit(env.storage, env.now(), env.navigationStartedAt?.())
    this.enteredAt = visit.enteredAt
    this.state.manualOnly = visit.manualOnly
  }

  get busy(): boolean { return this.state.phase !== 'idle' }

  private canAutoUpdate(): boolean {
    return this.env.now() - this.enteredAt < AUTO_UPDATE_WINDOW_MS
      && !this.state.manualOnly && !this.env.active() && !this.env.blocked()
  }

  available(): void {
    if (this.disposed) return
    this.state.pending = true
    this.reloadReady = false
    this.reconsider()
  }

  controllerChanged(): void {
    if (this.disposed) return
    this.reloadReady = true
    this.state.pending = true
    this.activationDone?.()
    this.reconsider()
  }

  reconsider(): void {
    if (this.disposed || !this.state.ready || !this.state.pending || this.busy) return
    // Remember that an active user needs a choice, even if this tab is hidden.
    if (!this.canAutoUpdate()) this.state.manualOnly = true
    if (!this.env.visible()) return
    if (this.canAutoUpdate()) void this.apply(false)
    else if (this.env.now() >= this.state.snoozedUntil) this.show()
  }

  show(): void {
    if (this.busy) return
    this.state.manualOnly = true
    this.state.expanded = true
    this.state.snoozedUntil = 0
    clearTimeout(this.reminder)
  }

  snooze(): void {
    if (this.busy) return
    this.state.manualOnly = true
    this.state.expanded = false
    this.state.snoozedUntil = this.env.now() + UPDATE_REMINDER_MS
    clearTimeout(this.reminder)
    this.reminder = setTimeout(() => this.reconsider(), UPDATE_REMINDER_MS)
  }

  apply(manual = true): Promise<void> {
    if (this.operation) return this.operation
    if (this.disposed || !this.state.ready || !this.state.pending || !this.env.visible()) return Promise.resolve()
    if (this.env.blocked() || (!manual && !this.canAutoUpdate())) {
      this.show()
      return Promise.resolve()
    }
    if (manual) this.state.manualOnly = true
    this.state.error = ''
    this.state.phase = 'saving'
    this.env.lock(true)
    this.operation = this.perform(manual).finally(() => {
      this.operation = undefined
      if (this.state.phase !== 'reloading') {
        this.state.phase = 'idle'
        this.env.lock(false)
        this.reconsider()
      }
    })
    return this.operation
  }

  private async perform(manual: boolean): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      await this.env.prepare(manual)
      if (!this.mayReload(manual)) return this.defer()
      this.state.phase = 'activating'
      if (!this.reloadReady) {
        const activated = new Promise<void>((resolve, reject) => {
          this.activationDone = resolve
          timer = setTimeout(() => reject(new Error('Activation timed out')), UPDATE_ACTIVATION_TIMEOUT_MS)
        })
        // Attach both handlers immediately: activation can finish before activate() returns.
        await Promise.all([activated, this.env.activate()])
      }
      if (!this.mayReload(manual)) return this.defer()
      try {
        this.env.storage().setItem(UPDATE_VISIT_KEY, JSON.stringify({
          startedAt: this.enteredAt, reloadedAt: this.env.now(), manualOnly: this.state.manualOnly,
        }))
      } catch {
        // Without a continuation marker, automatic reload could renew its own window.
        if (!manual) return this.defer()
      }
      this.state.phase = 'reloading'
      this.env.reload()
    } catch {
      const error = this.state.phase === 'saving' ? 'save' : 'activation'
      this.state.phase = 'idle'
      this.state.error = error
      this.show()
      try { this.env.storage().removeItem(UPDATE_VISIT_KEY) } catch { /* Best effort. */ }
    } finally {
      clearTimeout(timer)
      this.activationDone = undefined
    }
  }

  private mayReload(manual: boolean): boolean {
    return !this.disposed && this.env.visible() && !this.env.active() && !this.env.blocked()
      && (manual || this.canAutoUpdate())
  }

  private defer(): void {
    this.state.manualOnly = true
    this.state.phase = 'idle'
    if (this.env.visible()) this.show()
  }

  dispose(): void {
    this.disposed = true
    clearTimeout(this.reminder)
    this.activationDone?.()
  }
}
